import { ReorderableFormSectionChild, TagGroup, UseModalHook } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { observer } from "mobx-react";
import { ConfigureBidGroupsStepQuery } from "src/routes/bids/plan-based-bid-package/components/2.ConfigureBidGroupsStep.generated";
import { AddOptionModal } from "src/routes/bids/plan-based-bid-package/components/AddOptionModal";
import { BidPlanDimensionRows } from "src/routes/bids/plan-based-bid-package/components/BidPlanDimensionRows";
import {
  groupKey,
  PlanBasedBidPackageBidPlanGroup,
  PlanBasedBidPackageFormState,
  renumberGroups,
} from "src/routes/bids/plan-based-bid-package/utils";

type BuildOptionChildSectionParams = {
  formState: ObjectState<PlanBasedBidPackageFormState>;
  group: ObjectState<PlanBasedBidPackageBidPlanGroup>;
  dimensionResults: ConfigureBidGroupsStepQuery["potentialBidPlanDimensions"];
  potentialBidPlanOptions: ConfigureBidGroupsStepQuery["bidPackage"]["potentialBidPlanOptions"];
  openModal: UseModalHook["openModal"];
};

/**
 * Builds one Add-On Option's `childSection`. `id`/`orderField` make this natively draggable —
 * Beam's `FormSection` renders a drag handle and reorders `childSections` on drop by permuting
 * their `orderField` values directly, so no manual `DnDGrid`/`onReorder` wiring is needed here.
 */
export function buildOptionChildSection(params: BuildOptionChildSectionParams): ReorderableFormSectionChild {
  const { formState, group, dimensionResults, potentialBidPlanOptions, openModal } = params;
  const globalOptionId = group.globalOptionId.value;
  const option = potentialBidPlanOptions.find((o) => o.globalOption.id === globalOptionId);

  function openEditModal() {
    openModal({
      content: (
        <AddOptionModal
          formState={formState}
          dimensionResults={dimensionResults}
          potentialBidPlanOptions={potentialBidPlanOptions}
          editingGroup={group}
        />
      ),
    });
  }

  function removeOption() {
    // Remove by key, not globalOptionId, since the same option can have several groups
    const key = groupKey(group.value);
    const next = (formState.bidPlanGroups.value ?? []).filter((g) => groupKey(g) !== key);
    formState.bidPlanGroups.set(renumberGroups(next));
  }

  return {
    id: groupKey(group.value)!,
    orderField: group.order,
    title: option?.globalOption.name ?? "Option",
    actions: [
      { kind: "icon", icon: "pencil", onClick: openEditModal, label: "Edit Option" },
      { kind: "icon", icon: "trash", onClick: removeOption, label: "Remove Option" },
    ],
    fields: (
      <>
        <OptionPlanTags group={group} option={option} dimensionResults={dimensionResults} />
        <BidPlanDimensionRows group={group} dimensionResults={dimensionResults} />
      </>
    ),
  };
}

type OptionPlanTagsProps = {
  group: ObjectState<PlanBasedBidPackageBidPlanGroup>;
  option: ConfigureBidGroupsStepQuery["bidPackage"]["potentialBidPlanOptions"][number] | undefined;
  dimensionResults: ConfigureBidGroupsStepQuery["potentialBidPlanDimensions"];
};

/**
 * Tags the plans the group bids on: the add-on's plans that also surface every dimension, since lines only
 * generate there (e.g. an ADU zone narrows the add-on to the ADU plans).
 */
const OptionPlanTags = observer(function OptionPlanTags(props: OptionPlanTagsProps) {
  const { group, option, dimensionResults } = props;
  const dimensionPlanIds = (group.dimensions.value ?? []).map((d) => {
    const result = dimensionResults.find((r) =>
      d.globalOptionGroupId ? r.group?.id === d.globalOptionGroupId : r.type?.id === d.globalOptionTypeId,
    );
    return result?.productOfferings.map((po) => po.id) ?? [];
  });
  const planNames = (option?.readyPlanOptions ?? [])
    .map((rpo) => rpo.readyPlan)
    .uniqueByKey("id")
    .filter((rp) => dimensionPlanIds.every((ids) => ids.includes(rp.id)))
    .map((rp) => rp.name);
  return planNames.nonEmpty ? <TagGroup tags={planNames.map((name) => ({ text: name }))} /> : null;
});
